<template>
    <layout>
        <div class="py-5 bg-light_yellow"> 
            <div class="container relative px-4 pt-5 mx-auto">
                <ButtonComponent 
                    content="Atrás" 
                    :icon="'arrow_back_ios'"
                    :link="'/'"
                    type="2"
                />
                <div>
                    <CategoriesComponent :editable='false' :categories='fruit.categories' />
                </div>
                <div class="py-5">
                    <div class="flex justify-center md:justify-start">
                        <div class="flex items-center justify-center">
                            <button @click="addToFavorites()" v-if="$page.props.user != null && !isInFavorites"><img class="w-4 mr-4" src="/images/favorite-enriqueto.png" alt=""></button>
                            <button @click="removeToFavorites()" v-if="$page.props.user != null && isInFavorites"><img class="w-4 mr-4" src="/images/favorite-fill-enriqueto.png" alt=""></button>
                            <img
                                class="object-cover w-16 h-16 ml-4 bg-white border-2 rounded-full border-enriqueto_green"
                                :src="fruit.image"
                                alt=""
                            />
                            <h2 class="px-4 text-xl font-bold text-enriqueto_green">{{fruit.name}} <br> <span class="text-xs font-medium tracking-wide uppercase text-gray">Precio internacional de referencia</span><br><span class="text-2xl ">{{usePriceFormat(fruit.price)}}</span><span v-if="fruit.unit" class="ml-1 text-sm font-normal text-gray">/ {{fruit.unit}}</span></h2>
                        </div>
                    </div>
                </div>

                <section class="overflow-hidden bg-white border shadow-sm rounded-3xl border-light_gray">
                    <header class="flex flex-col gap-4 p-5 border-b md:p-6 md:flex-row md:items-center md:justify-between border-light_gray">
                        <div>
                            <p class="text-sm font-bold tracking-wider uppercase text-enriqueto_green">Comportamiento histórico</p>
                            <h2 class="mt-1 text-2xl font-bold text-enriqueto_green">Precio de referencia</h2>
                            <p class="mt-2 text-sm font-bold text-enriqueto_green">Tamaño del mercado: <span class="break-words">{{useMarketValueFormat(fruit.marketcap)}}</span></p>
                        </div>
                        <div id="days-filter" class="flex flex-wrap justify-center p-1 bg-light_yellow rounded-xl">
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==7}" v-on:click="getReportsByDays(7)">7D</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==15}" v-on:click="getReportsByDays(15)">15D</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==30}" v-on:click="getReportsByDays(30)">1M</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==90}" v-on:click="getReportsByDays(90)">3M</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==180}" v-on:click="getReportsByDays(180)">6M</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==365}" v-on:click="getReportsByDays(365)">1Y</button>
                            <button class="inline-block px-4 py-2 mx-2 font-bold rounded filter-button text-gray hover:bg-blue-700" :class="{'bg-yellow':active_filter==500}" v-on:click="getReportsByDays(500)">ALL</button>
                        </div>
                    </header>
                    <div class="w-full p-4 h-80 md:h-96 md:p-6">
                        <vue3-chart-js
                            ref="priceChartRef"
                            :id="PriceChart.id"
                            :type="PriceChart.type"
                            :data="PriceChart.data"
                            :options="PriceChart.options"
                            @before-render="beforeRenderLogic"
                        ></vue3-chart-js>
                    </div>
                </section>

            </div>
        </div>
        <div class="container relative px-4 pt-5 mx-auto">
            <RegionalPriceExplorer v-if="regional_comparison_enabled" :regional-prices="regional_prices" />
            <PublicOffersTable :offers="offers" />
            <section v-if="hasProductInformation || hasTaxonomy" class="p-5 mt-8 border md:p-8 rounded-3xl border-light_gray bg-light_yellow">
                <div class="max-w-3xl">
                    <p class="text-sm font-bold tracking-wider uppercase text-light_green">Información del producto</p>
                    <h2 class="mt-2 text-enriqueto_green">Ficha informativa</h2>
                    <p class="mt-2 text-gray">Datos de referencia para conocer mejor este producto, sus características y sus usos.</p>
                </div>

                <div class="grid gap-5 mt-6 md:grid-cols-2">
                    <article v-if="fruit.scientific_name || hasTaxonomy" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Nombre científico</h3>
                        <p v-if="fruit.scientific_name" class="mt-2 italic leading-relaxed whitespace-pre-line">{{fruit.scientific_name}}</p>
                        <p v-else class="mt-2 italic leading-relaxed">{{taxonomyScientificNames}}</p>
                        <CoffeeTaxonomyExplorer
                            v-if="hasTaxonomy"
                            :terms="taxonomy_terms"
                            :product-name="fruit.name"
                            :spreadsheet-url="taxonomy_source.url"
                        />
                    </article>
                    <article v-if="fruit.origin" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Origen y distribución</h3>
                        <p class="mt-2 leading-relaxed whitespace-pre-line">{{fruit.origin}}</p>
                    </article>
                    <article v-if="fruit.seasonality" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Temporada y disponibilidad</h3>
                        <p class="mt-2 leading-relaxed whitespace-pre-line">{{fruit.seasonality}}</p>
                    </article>
                    <article v-if="fruit.uses" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Usos y aplicaciones</h3>
                        <p class="mt-2 leading-relaxed whitespace-pre-line">{{fruit.uses}}</p>
                    </article>
                    <article v-if="fruit.nutritional_information" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Información nutricional</h3>
                        <p class="mt-2 leading-relaxed whitespace-pre-line">{{fruit.nutritional_information}}</p>
                    </article>
                    <article v-if="fruit.information_sources" class="p-5 bg-white shadow-sm rounded-2xl">
                        <h3 class="font-bold text-enriqueto_green">Fuentes y referencias</h3>
                        <p class="mt-2 text-sm leading-relaxed whitespace-pre-line">{{fruit.information_sources}}</p>
                    </article>
                </div>
            </section>
        </div>
    </layout>
</template>

<script>
import Layout from '../../Components/Shared/Layout.vue'
import Vue3ChartJs from '@j-t-mcc/vue3-chartjs'
import { ref, watch } from 'vue'
import { Inertia } from '@inertiajs/inertia'
import { Link } from '@inertiajs/inertia-vue3'
import useApi from '../../Services/useApi'
import usePriceFormat, { useMarketValueFormat, usePriceCovert } from '../../Services/usePriceFormat'
import { useStore } from 'vuex'
import { computed } from 'vue'
import CategoriesComponent from '../../Components/Shared/CategoriesComponent.vue'
import ButtonComponent from '../../Components/Shared/ButtonComponent.vue'
import PublicOffersTable from '../../Components/Market/PublicOffersTable.vue'
import RegionalPriceExplorer from '../../Components/Market/RegionalPriceExplorer.vue'
import CoffeeTaxonomyExplorer from '../../Components/Classification/CoffeeTaxonomyExplorer.vue'

export default {
    components: { Link,Layout,Vue3ChartJs,CategoriesComponent,ButtonComponent,PublicOffersTable,RegionalPriceExplorer,CoffeeTaxonomyExplorer },
    props: ['fruit','offers','regional_prices','regional_comparison_enabled','taxonomy_terms','taxonomy_source'],
    setup (props) {
    const priceChartRef = ref(null)
    const active_filter = ref(7)
    const api = useApi()
    const store = useStore()
    const hasProductInformation = computed(() => [
        'scientific_name',
        'origin',
        'seasonality',
        'uses',
        'nutritional_information',
        'information_sources'
    ].some(field => Boolean(props.fruit[field] && String(props.fruit[field]).trim())))
    const hasTaxonomy = computed(() => Array.isArray(props.taxonomy_terms) && props.taxonomy_terms.length > 0)
    const taxonomyScientificNames = computed(() => {
        const species = props.taxonomy_terms
            .filter(term => String(term.rank || '').toLocaleUpperCase('es').includes('ESPECIE'))
            .map(term => term.name)
        return species.length ? species.join(' · ') : props.taxonomy_terms[props.taxonomy_terms.length - 1]?.name
    })
    watch(()=>store.state.currency, function() {
        getReportsByDays(active_filter.value)
    });
    let reports = props.fruit.last_reports;
    let value_report = reports.map(({ value }) => value)
    let date_report = reports.map(({ created_at }) => created_at)
    if(reports.length<2){
        value_report = [
            props.fruit.price,
            props.fruit.price
        ]
        let date = new Date(Date.now()).toLocaleString().split(',')[0]
        date_report = [
            date,
            date
        ]
    }
    const dark_mode = computed(() => store.state.dark_mode)
    let greenColor = 'rgba(3, 102, 46,0.3)'
    let redColor = 'rgba(255,0,0,0.5)'
    let borderColor = greenColor
    let color = greenColor
    if(dark_mode.value){
        greenColor = '#22D66F'
        redColor = 'rgba(255,0,0,0.5)'
        borderColor = greenColor
        color = greenColor
    }
    if(props.fruit.increase<0){
        borderColor = redColor
        color = redColor
    }
    const graphOptions = {
            elements: {
                line: {
                    tension: 0.6
                }
            },
            plugins: {
                legend: {
                    display: false,
                } 
            },
            responsive: true,
            maintainAspectRatio: false,
            interaction: {
            mode: 'index',
            intersect: false
            },
            scales: {
            x: {
                display: true,
            },
            y: {
                display: true,
            }
            }
        };
    const PriceChart = {
        id: 'lineGraph'+props.fruit.id,
        type: 'line',
        data: {
            labels: date_report,
            datasets: [{ 
                data: value_report,
                borderColor: borderColor,
                fill: true,
                pointBackgroundColor: borderColor,
                pointRadius: 2.5,
                backgroundColor: color,
                hoverBackgroundColor: 'rgba(33, 150, 83, 0.6)',
                borderWidth: 1
            }
            ]
        },
        options: graphOptions,
    }

    const beforeRenderLogic = (event) => {
      //...
      //if(a === b) {
      //  event.preventDefault()
      //}
    }

    const getReportsByDays = (days)=>{
        active_filter.value = days
        api.getReports(props.fruit.id,days)
        .then(function (response) {
            console.log(response.reports);
            let reports = response.reports;
            let value_report = reports.map(({ value }) => {return usePriceCovert(value,store.state.currency)})
            let date_report = reports.map(({ created_at }) => created_at)
            if(response.reports.length<2){
                value_report = [
                    props.fruit.price,
                    props.fruit.price
                ]
                let date = new Date(Date.now()).toLocaleString().split(',')[0]
                date_report = [
                    date,
                    date
                ]
            }
            PriceChart.data.labels = date_report
            PriceChart.data.datasets[0].data = value_report
            priceChartRef.value.update(250)
        })
    }

    const addToFavorites = () => {
        let values = {fruit_id:props.fruit.id}
        api.addFruitToFavorites(values).then((res)=>{
            let favorites = res.data.favorites.map(item=>item.id)
            store.commit('setFavorites',favorites)
            Inertia.reload()
        })
    }
    const removeToFavorites = () => {
        let values = {fruit_id:props.fruit.id}
        api.removeFruitToFavorites(values).then((res)=>{
            let favorites = res.data.favorites.map(item=>item.id)
            store.commit('setFavorites',favorites)
            Inertia.reload()
        })
    }
    const isInFavorites = computed(() => store.state.favorites.includes(props.fruit.id));

        return {
            PriceChart,
            beforeRenderLogic,
            getReportsByDays,
            priceChartRef,
            active_filter,
            usePriceFormat,
            useMarketValueFormat,
            addToFavorites,
            removeToFavorites,
            isInFavorites,
            hasProductInformation,
            hasTaxonomy,
            taxonomyScientificNames,
        }
    },
}
</script>

<style>

</style>
